import { boxLineTimes } from '../../utils/boxLinePoints';
// frontend/src/components/charts/FuturesChartSimulation.js
import { remainingAfterExit } from '../../utils/autoTradingExits';
import { formatExitReason } from '../../utils/exitReasonLabel';
import {
  boxLineColor,
  boxLineStroke,
  boxLineActive,
} from '../../utils/boxLineStyle';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import StockTools from 'highcharts/modules/stock-tools';
import PriceIndicator from 'highcharts/modules/price-indicator';
import AnnotationsAdvanced from 'highcharts/modules/annotations-advanced';
import FullScreen from 'highcharts/modules/full-screen';
import Accessibility from 'highcharts/modules/accessibility';
import axios from 'axios';
import AutoTradingDetailHistoryPage from '../pages/AutoTradingDetailHistoryPage';
import useSimOverlay from '../../hooks/useSimOverlay';
import SimRunsListTable from '../tables/SimRunsListTable';

// 모듈 초기화 (내보내기가 default인지 아닌지 조건 처리)
if (typeof StockTools === 'function') {
  StockTools(Highcharts);
}
if (typeof AnnotationsAdvanced === 'function') {
  AnnotationsAdvanced(Highcharts);
}
if (typeof PriceIndicator === 'function') {
  PriceIndicator(Highcharts);
}
if (typeof FullScreen === 'function') {
  FullScreen(Highcharts);
}
if (typeof Accessibility === 'function') {
  Accessibility(Highcharts);
}

// 라운드피겨 박스라인 설정
const ROUND_FIGURE_BOX_LINES_VALUE = [
  10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000, 18000, 19000, 20000,
  21000, 22000, 23000, 24000, 25000, 26000, 27000, 28000, 29000, 30000, 31000,
  32000, 33000, 34000, 35000, 36000, 37000, 38000, 39000, 40000,
];

// 청산 사유 한글 매핑
const EXIT_REASON_LABELS = {
  stop_loss: '손절',
  break_even: '본청',
  power_zero: '강도 0 청산 ',
  market_end: '장마감 청산',
  power_80: '강도80%청산',
  power_85: '강도85%청산',
  power_90: '강도90%청산',
  premarket_profit_over_100p: '프리장100p익절',
  price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
};

// close 위에 두고 싶은 0선-간격(픽셀)
const ZERO_GAP_PX = 48;

// object/boolean 혼용 visiblePlotLines에서 "체크 여부"를 안전하게 뽑는 헬퍼
function isLineChecked(val) {
  if (typeof val === 'object' && val !== null) return !!val.checked;
  return !!val;
}

export default function FuturesChartSimulation({
  visiblePlotLines,
  onToggleLine,
}) {
  /* --------------------------------------------- */
  /* -------------- { 상태 관리 파트 } -------------- */
  /* --------------------------------------------- */
  const chartRef = useRef(null);

  // 현재 표시중인 세션 범위 (scatter 필터/labels 등 공통)
  const sessionStartMsRef = useRef(null);
  const sessionEndMsRef = useRef(null);
  const calculationRunIdRef = useRef(null);

  // boxline range 마커
  const rangeStartXRef = useRef(null);
  const rangeEndXRef = useRef(null);
  const rangeMarkersRef = useRef({
    startLineId: null,
    endLineId: null,
    bandId: null,
  }); // 플롯 라인/밴드 ID 기억용

  // 상태
  const [chartOptions, setChartOptions] = useState({});
  const [latestClose, setLatestClose] = useState(0);

  const [boxLines, setBoxLines] = useState([]);
  const [isBoxLines, setIsBoxLines] = useState([]);

  const [highestVolume, setHighestVolume] = useState([]);
  const [tradeLabels, setTradeLabels] = useState([]); // 자동거래 라벨 annotation.labels
  const [simListOpen, setSimListOpen] = useState(false); // 시뮬레이션 리스트 모달 상태

  // 세션 이동 UI
  const [targetDate, setTargetDate] = useState(() => {
    // 기본값: 오늘(KST) YYYY-MM-DD
    const kst = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
    );
    const yyyy = kst.getFullYear();
    const mm = String(kst.getMonth() + 1).padStart(2, '0');
    const dd = String(kst.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [loadingSession, setLoadingSession] = useState(false);

  // 강도(파워) 표시/기록 관련
  const [customRangeArmed, setCustomRangeArmed] = useState(false); // Custom START 'Range' 버튼 선택 상태
  const [rangeSelectPhase, setRangeSelectPhase] = useState('idle'); // 'idle', 'start', 'end'
  const customRangeArmedRef = useRef(false);
  const rangePhaseRef = useRef('idle');

  const [currentCumulativePower, setCurrentCumulativePower] = useState(0);
  const [powerMinMax, setPowerMinMax] = useState({ minPower: 0, maxPower: 0 });
  const [powerActive, setPowerActive] = useState(false);

  useEffect(() => {
    customRangeArmedRef.current = customRangeArmed;
  }, [customRangeArmed]);
  useEffect(() => {
    rangePhaseRef.current = rangeSelectPhase;
  }, [rangeSelectPhase]);

  /* ------------------------------------------- */
  /* --------------- { 유틸/헬퍼 } --------------- */
  /* ------------------------------------------- */

  // 서버가 tz-aware(+09:00) ISO를 기대하므로 KST 고정 ISO 생성
  function getKSTIsoString(date) {
    const formatted = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date); // "2025-04-17 07:00:00"
    return formatted.replace(' ', 'T') + '+09:00';
  }

  // 특정 "순간" 기준 시카고 DST 여부 (과거/미래 날짜도 판정 가능)
  function isDstInChicagoAt(date) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.some((p) => p.value === 'CDT');
  }

  // YYYY-MM-DD(=KST 캘린더 날짜)를 "세션 시작일"로 해석하여 세션(start~end) ms 반환
  // DST면 07:00~(익일)06:00, 비DST면 08:00~(익일)07:00 (총 23h)
  function getSessionWindowMsKST(dateStr) {
    // DST 판정은 해당 날짜의 "정오(KST)"를 기준으로 안전하게 판단
    const noonKst = new Date(`${dateStr}T12:00:00+09:00`);
    const isDst = isDstInChicagoAt(noonKst);

    const startHour = isDst ? 7 : 8;
    const start = new Date(
      `${dateStr}T${String(startHour).padStart(2, '0')}:00:00+09:00`,
    );
    const startMs = start.getTime();
    const endMs = startMs + 23 * 60 * 60 * 1000; // 하드 엔드(23시간 뒤)
    return { startMs, endMs, isDst };
  }

  const fmt2 = (n) =>
    n == null
      ? ''
      : Number(n).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const mapExitReason = (reason, event) =>
    formatExitReason(reason, event, EXIT_REASON_LABELS);

  /* -------------------------------------------------- */
  /* ----------- { 시뮬레이션 오버레이 관련 파트 } ----------- */
  /* -------------------------------------------------- */
  const {
    simEnabled,
    setSimEnabled,
    simRunId,
    setSimRunId,
    simLoading,
    includeBoxlines,
    setIncludeBoxlines,
    includeOrders,
    setIncludeOrders,
    includeRisk,
    setIncludeRisk,
    refreshSimOverlay,
    removeSimOverlay,
  } = useSimOverlay({
    apiUrl: process.env.REACT_APP_API_URL,
    chartRef,
  });

  /* ------------------------------------------- */
  /* ---------- { 자동거래 라벨 표시 파트 } ---------- */
  /* ------------------------------------------- */

  // history-range API로 구간 내 전략을 받아 라벨 배열 생성
  async function fetchTradeLabelsForRange(startMs, endMs) {
    try {
      const startIso = getKSTIsoString(new Date(startMs));
      const endIso = getKSTIsoString(new Date(endMs));

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auto-trading/strategies/history-range`,
        {
          params: {
            start: startIso,
            end: endIso,
            include_open_spanning: true, // 구간을 걸치는 열린 포지션 포함
          },
        },
      );

      const strategies = res.data ?? [];
      // events_history에서 enter/exit 추출 → 라벨 포인트 생성
      const labels = [];

      for (const s of strategies) {
        const directionLong = s.direction === 'long' || s.direction === true;
        const dirLabel = directionLong ? '매수' : '매도';

        const evts = Array.isArray(s.events_history)
          ? [...s.events_history]
          : [];
        evts.sort((a, b) => new Date(a.t) - new Date(b.t));

        let open = null;
        for (const e of evts) {
          if (e.type === 'enter') {
            open = {
              t: e.t,
              price: Number(e.price),
              open_qty: Number(e.qty ?? 1),
            };
            // 진입 라벨
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(open.t).getTime(),
                y: open.price,
              },
              text: `진입(${dirLabel})\n${fmt2(open.price)}`,
              backgroundColor: directionLong
                ? 'rgba(230, 56, 56, 0.341)'
                : 'rgba(37, 100, 235, 0.337)',
              borderColor: directionLong ? '#dc2626' : '#2563eb',
              borderWidth: 1,
              shape: 'callout',
              style: {
                fontSize: '10px',
                fontWeight: '600',
                color: 'black',
              },
              // 약간 위/아래로 오프셋
              // y: directionLong ? -10 : 10,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
          } else if (e.type === 'exit' && open) {
            const exitPrice =
              e.price != null
                ? Number(e.price)
                : s.exit_price != null
                  ? Number(s.exit_price)
                  : null;

            const profit =
              typeof e.profit === 'number'
                ? e.profit
                : exitPrice != null
                  ? (exitPrice - open.price) * (directionLong ? 1 : -1)
                  : null;

            const reasonTxt = e.reason || s.exit_reason || '';

            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(e.t).getTime(),
                y: exitPrice ?? open.price,
              },
              text: `${mapExitReason(reasonTxt, e)}\n${fmt2(exitPrice ?? '')}`,
              backgroundColor:
                profit != null
                  ? profit >= 0
                    ? 'rgba(16,185,129,0.14)'
                    : 'rgba(239,68,68,0.14)'
                  : 'rgba(107,114,128,0.14)',
              borderColor:
                profit != null
                  ? profit >= 0
                    ? '#10b981'
                    : '#ef4444'
                  : '#6b7280',
              borderWidth: 1,
              shape: 'callout',
              style: { fontSize: '10px', fontWeight: '600', color: 'black' },
              y: profit != null ? (profit >= 0 ? -10 : 10) : 0,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
            const remainingQty = remainingAfterExit(open, e, e.qty);
            open =
              remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
          }
        }
      }

      setTradeLabels(labels);
    } catch (e) {
      console.error('자동거래 라벨 조회 실패:', e);
      setTradeLabels([]);
    }
  }

  /* ------------------------------------------------ */
  /* ------------- { 세션 전용 데이터 로딩 } ------------- */
  /* ------------------------------------------------ */

  async function fetchSessionOverlay(startMs, endMs, calculationRunId = null) {
    const startIso = getKSTIsoString(new Date(startMs));
    const endIso = getKSTIsoString(new Date(endMs));
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/boxlines/session-overlay`,
      {
        params: {
          start: startIso,
          end: endIso,
          calculation_run_id: calculationRunId,
          include_replay: !calculationRunId,
        },
      },
    );

    setBoxLines(Array.isArray(data?.box_lines) ? data.box_lines : []);
    setIsBoxLines(Array.isArray(data?.is_box_lines) ? data.is_box_lines : []);
  }

  async function fetchPriceRange(startMs, endMs) {
    const startIso = getKSTIsoString(new Date(startMs));
    const endIso = getKSTIsoString(new Date(endMs));

    // 백엔드가 end를 지원하는 경우 우선 사용
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: startIso, end: endIso },
        },
      );
      const rows = Array.isArray(data) ? data : [];
      return rows
        .slice()
        .sort(
          (a, b) => new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
        );
    } catch (e) {
      // end 미지원일 때: start~현재를 받고 클라이언트에서 end로 필터
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: startIso },
        },
      );
      const rows = (Array.isArray(data) ? data : []).filter((r) => {
        const t = new Date(r.timestamp_korean).getTime();
        return t <= endMs;
      });
      return rows
        .slice()
        .sort(
          (a, b) => new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
        );
    }
  }

  async function fetchTradeLabelsForRange(startMs, endMs) {
    try {
      const startIso = getKSTIsoString(new Date(startMs));
      const endIso = getKSTIsoString(new Date(endMs));

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auto-trading/strategies/history-range`,
        {
          params: {
            start: startIso,
            end: endIso,
            include_open_spanning: true,
          },
        },
      );

      const strategies = res.data ?? [];
      const labels = [];

      for (const s of strategies) {
        const directionLong = s.direction === 'long' || s.direction === true;
        const dirLabel = directionLong ? '매수' : '매도';

        const evts = Array.isArray(s.events_history)
          ? [...s.events_history]
          : [];
        evts.sort((a, b) => new Date(a.t) - new Date(b.t));

        let open = null;
        for (const e of evts) {
          if (e.type === 'enter') {
            open = {
              t: e.t,
              price: Number(e.price),
              open_qty: Number(e.qty ?? 1),
            };
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(open.t).getTime(),
                y: open.price,
              },
              text: `진입(${dirLabel})\n${fmt2(open.price)}`,
              backgroundColor: directionLong
                ? 'rgba(230,56,56,0.20)'
                : 'rgba(37,100,235,0.20)',
              borderColor: directionLong ? '#dc2626' : '#2563eb',
              borderWidth: 1,
              shape: 'callout',
              style: { fontSize: '10px', fontWeight: '600', color: 'black' },
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
          } else if (e.type === 'exit' && open) {
            const exitPrice =
              e.price != null
                ? Number(e.price)
                : s.exit_price != null
                  ? Number(s.exit_price)
                  : null;

            const profit =
              typeof e.profit === 'number'
                ? e.profit
                : exitPrice != null
                  ? (exitPrice - open.price) * (directionLong ? 1 : -1)
                  : null;

            const reasonTxt = e.reason || s.exit_reason || '';
            labels.push({
              point: {
                xAxis: 0,
                yAxis: 0,
                x: new Date(e.t).getTime(),
                y: exitPrice ?? open.price,
              },
              text: `${mapExitReason(reasonTxt, e)}\n${fmt2(exitPrice ?? '')}`,
              backgroundColor:
                profit != null
                  ? profit >= 0
                    ? 'rgba(16,185,129,0.16)'
                    : 'rgba(239,68,68,0.16)'
                  : 'rgba(107,114,128,0.14)',
              borderColor:
                profit != null
                  ? profit >= 0
                    ? '#10b981'
                    : '#ef4444'
                  : '#6b7280',
              borderWidth: 1,
              shape: 'callout',
              style: { fontSize: '10px', fontWeight: '600', color: 'black' },
              y: profit != null ? (profit >= 0 ? -10 : 10) : 0,
              verticalAlign: 'bottom',
              includeInDataExport: false,
            });
            const remainingQty = remainingAfterExit(open, e, e.qty);
            open =
              remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
          }
        }
      }

      setTradeLabels(labels);
    } catch (e) {
      console.error('자동거래 라벨 조회 실패:', e);
      setTradeLabels([]);
    }
  }

  const buildChartOptions = useCallback(
    (priceData, startMs, endMs) => {
      // 기본 xAxis는 세션 범위로 제한
      return {
        credits: {
          enabled: false,
        },
        chart: {
          height: (8 / 16) * 100 + '%',
          zooming: {
            type: 'x',
          },
        },
        palette: {
          colorScheme: 'light',
        },
        time: {
          timezone: 'Asia/Seoul',
        },
        rangeSelector: {
          selected: 5,
          buttons: [
            {
              type: 'minute',
              count: 30,
              text: '30m',
            },
            {
              type: 'hour',
              count: 1,
              text: '1h',
            },
            {
              type: 'hour',
              count: 2,
              text: '2h',
            },
            {
              type: 'hour',
              count: 4,
              text: '4h',
            },
            {
              type: 'hour',
              count: 8,
              text: '8h',
            },
            {
              type: 'day',
              count: 1,
              text: '1D',
            },
            {
              type: 'all',
              text: 'All',
            },
          ],
        },
        tooltip: {
          backgroundColor: '#00ff0000',
          shadow: false,
          split: false,
          shape: 'square',
          headerShape: 'callout',
          shared: true,
          snap: 0,
          positioner: () => ({ x: 60, y: 30 }),
        },
        plotOptions: {
          series: {
            states: {
              hover: {
                enabled: false,
              },
            },
            lastPrice: {
              enabled: true,
              color: '#00ff0000',
              label: {
                enabled: true,
                borderColor: 'grey',
                borderWidth: 1,
                backgroundColor: '#00ff0000',
                format: '{value:.2f}',
                style: {
                  color: 'black',
                  fontWeight: 'bold',
                  textAlign: 'right',
                },
              },
            },
          },
        },
        annotations: [],
        yAxis: [
          // Main Chart
          {
            title: { text: '가격' },
            height: '100%',
            lineWidth: 2,
            opposite: true,
            labels: {
              align: 'left',
              x: 2,
              format: '{value:.2f}',
            },
            crosshair: {
              label: {
                enabled: true,
                format: '{value:.2f}',
              },
              snap: false,
            },
            plotLines: [],
          },
          // Sub Chart
          {
            title: { text: '거래 강도' },
            top: '10%',
            height: '30%',
            // offset: 0,
            lineWidth: 2,
            // 반응형 y축 범위: 초기 기본 범위 (추후 useEffect로 업데이트)
            min: -50,
            max: 50,
            showLastLabel: true,
            crosshair: {
              // label: {
              //   enabled: true,
              //   format: '{value:.0f}',
              // },
              snap: false,
            },
            labels: {
              align: 'left',
              // format: '{value:.0f}',
            },
            plotLines: [],
          },
        ],
        xAxis: [
          {
            overscroll: 200 * 1000,
            gridLineWidth: 1,
            tickInterval: 3600 * 1000, // 1시간 = 3600000 밀리초
            minRange: (10 / 60) * 1 * 3600000, // x축 최소 범위: 10분
            align: 'high',
            type: 'datetime',
            // min: startMs,
            // max: endMs,
            events: {
              afterSetExtremes: function (e) {
                // e.min/e.max는 epoch ms
                // 너무 잦은 호출이 부담이면 debounce를 추가
                // 시뮬 오버레이는 debounce된 갱신
                refreshSimOverlay({ min: e.min, max: e.max });
              },
            },
          },
          // {
          //   overscroll: 200 * 1000,
          //   gridLineWidth: 1,
          //   tickInterval: 3600 * 1000, // 1시간 = 3600000 밀리초
          //   minRange: (10 / 60) * 1 * 3600000, // x축 최소 범위: 10분
          //   align: 'high',
          //   type: 'datetime',
          //   min: xAxisOptions.min,
          //   max: xAxisOptions.max,
          // },
        ],
        series: [
          {
            type: 'ohlc',
            name: '체결가격',
            data: priceData,
            tooltip: { valueDecimals: 2 },
            lineWidth: 1,
            yAxis: 0,
            color: 'black',
            pointInterval: 1000, // 1초
            dataGrouping: {
              // enabled: false,
              groupPixelWidth: 3,
            },
          },
          {
            type: 'line',
            name: '거래 강도',
            data: [], // 초기에는 빈 배열; START 후 업데이트
            yAxis: 1,
            tooltip: { valueDecimals: 0 },
            color: 'blue',
            pointInterval: 1000, // 1초,
            dataGrouping: {
              // enabled: false,
              groupPixelWidth: 3,
            },
          },
        ],
        stockTools: {
          gui: {
            enabled: true,
          },
        },
      };
    },
    [refreshSimOverlay],
  );

  const loadSessionByDate = useCallback(
    async (dateStr) => {
      const { startMs, endMs } = getSessionWindowMsKST(dateStr);
      // Capture scope before awaiting prices; another selection can change the ref.
      const calculationRunId = calculationRunIdRef.current;

      setLoadingSession(true);

      // 이동 시 기존 표시 초기화 (핵심)
      setBoxLines([]);
      setIsBoxLines([]);
      setTradeLabels([]);

      // Power도 초기화
      resetPowerOnly();

      // SIM overlay도 기존 것을 지우는 게 안전
      try {
        removeSimOverlay();
      } catch (_) {}

      sessionStartMsRef.current = startMs;
      sessionEndMsRef.current = endMs;

      try {
        // 1) 가격 데이터
        const rows = await fetchPriceRange(startMs, endMs);
        const priceData = rows.map((item) => [
          new Date(item.timestamp_korean).getTime(),
          +item.open,
          +item.high,
          +item.low,
          +item.close,
        ]);

        const lastClose = rows.length ? Number(rows.at(-1).close) : 0;
        setLatestClose(Number.isFinite(lastClose) ? lastClose : 0);

        // 2) 차트 옵션 구성(세션 범위로 제한)
        setChartOptions(buildChartOptions(priceData, startMs, endMs));

        // 3) 박스라인/활성박스라인/비활성 most id (세션-overlay)
        await fetchSessionOverlay(startMs, endMs, calculationRunId);

        // 4) 자동거래 라벨(세션 구간 1회 조회)
        await fetchTradeLabelsForRange(startMs, endMs);

        // 5) overlay는 차트가 존재해야 안전하므로 다음 tick에서 1회 갱신
        setTimeout(() => {
          const chart = chartRef.current?.chart;
          if (chart?.xAxis?.[0]) {
            refreshSimOverlay({
              min: chart.xAxis[0].min,
              max: chart.xAxis[0].max,
            });
          }
        }, 0);
      } catch (e) {
        console.error('세션 로딩 실패:', e);
        alert('세션 데이터를 불러오지 못했습니다.');
        setChartOptions({});
      } finally {
        setLoadingSession(false);
      }
    },
    [buildChartOptions, refreshSimOverlay, removeSimOverlay],
  );

  // SimRunsListTable에서 행 클릭 시: simRunId, targetDate 세팅 + 모달 닫기 + 세션 로드
  const handleSelectSimRun = useCallback(
    (row) => {
      const runId = row?.id != null ? String(row.id) : '';
      const dateStr = row?.start_kst ? String(row.start_kst).slice(0, 10) : '';

      calculationRunIdRef.current = row?.calculation_run_id || null;
      if (runId) setSimRunId(runId);
      if (dateStr) {
        setTargetDate(dateStr);
        loadSessionByDate(dateStr);
      }

      setSimListOpen(false);
    },
    [setSimRunId, loadSessionByDate],
  );

  /* ------------------------ Top Volume(기존 기능 유지) ------------------------ */

  const fetchHighestVolume = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/tables/top_volume_by_interval`,
      );

      setHighestVolume(response.data);

      Object.keys(response.data).forEach((interval_label) => {
        response.data[interval_label].forEach((row) => {
          const key = `${interval_label}_${row.rank}`;
          if (visiblePlotLines[key] === undefined) {
            onToggleLine(key, row.rank <= 5, {
              interval: interval_label,
              rank: row.rank,
              timestamp_korean: row.timestamp_korean,
              close: row.close,
              volume: row.volume,
              power: row.power,
            });
          }
        });
      });
    } catch (error) {
      console.error('highestVolume data fetch error:', error);
      setHighestVolume([]);
    }
  }, [visiblePlotLines, onToggleLine]);

  useEffect(() => {
    // 최초 1회: TopVolume은 기존 로직 유지
    fetchHighestVolume();
  }, [fetchHighestVolume]);

  /* --------------------------------------------------- */
  /* ---------------- plotLines 계산 관련 ---------------- */
  /* --------------------------------------------------- */

  // top volume line 파트
  const calTopVolumePlotLines = () => {
    // 시간대별 색상 매핑
    const colorMapping = {
      1: '#FF0000', // 빨간색
      2: '#FFA500', // 주황색
      3: '#ffd907', // 노란색
      4: '#008000', // 녹색
      5: '#800080', // 보라색
      // 6: "#0000FF", // 파란색
      // 7: "#060c8d", // 남색
    };

    // 그룹별로 plotLine 객체 생성
    const newPlotLines = [];

    // highestVolume은 {"0-1h": [...], "1-2h": [...], ... } 형태
    Object.keys(highestVolume).forEach((interval_label) => {
      const rowArray = highestVolume[interval_label];
      // rowArray는 해당 interval_label의 배열
      rowArray.forEach((item) => {
        // composite key 생성
        const toggleKey = `${interval_label}_${item.rank}`;
        // 체크 박스가 해제되어 있으면 표시하지 않음
        if (!isLineChecked(visiblePlotLines[toggleKey])) return;

        // plotLines 생성
        const value = parseFloat(item.close) || 0;
        newPlotLines.push({
          value,
          color: colorMapping[item.rank] || 'gray',
          width: 2,
        });
      });
    });

    return newPlotLines;
  };

  // is_boxline 파트
  const calBoxPlotLines = () =>
    isBoxLines
      .filter((line) => line.is_boxline && !isNaN(line.close_box))
      .map((line, index) => {
        // 1) 최소 타임스탬프(ms)
        const minTsMs = Math.min(...boxLineTimes(line));

        // 2) KST 기준의 '연-월-일'만 떼어 날짜 차이 계산
        const toKSTDateOnly = (ms) => {
          const kst = new Date(
            new Date(ms).toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
          );
          // 자정(00:00:00)으로 내림
          return new Date(
            Date.UTC(kst.getFullYear(), kst.getMonth(), kst.getDate()),
          );
        };

        const todayKST = toKSTDateOnly(Date.now());
        const targetKST = toKSTDateOnly(minTsMs);
        const MS_PER_DAY = 24 * 60 * 60 * 1000; // 86,400,000ms
        const daysDelta = Math.round((todayKST - targetKST) / MS_PER_DAY); // 항상 캘린더 일수 차이

        return {
          id: `box-${index}`,
          value: +line.close_box,
          color: boxLineColor(line),
          width: boxLineActive(line) ? 2 : 1,
          dashStyle: boxLineActive(line) ? 'Solid' : 'Dash',
          label: {
            text: `${daysDelta}일전 - ${line.close_box}`,
            align: 'left',
          },
        };
      });

  // 4) 라운드 피겨 박스라인 파트
  const calRoundFigureBoxLines = () =>
    ROUND_FIGURE_BOX_LINES_VALUE.map((item) => {
      return {
        id: `round-${item}`,
        value: +item,
        color: 'black',
        width: 2,
        dashStyle: 'solid',
        label: {
          text: `라운드피겨 (${item})`,
          align: 'left',
        },
      };
    });

  /* plotLines에 대한 useMemo 파트 */
  const allPlotLines = useMemo(() => {
    return [
      ...calTopVolumePlotLines(),
      ...calBoxPlotLines(),
      ...calRoundFigureBoxLines(),
    ];
  }, [
    // boxLines,
    isBoxLines,
    highestVolume,
    visiblePlotLines,
    // ROUND_FIGURE_BOX_LINES_VALUE,
  ]);

  // plotLines 반영
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    const yAxis = chart.yAxis?.[0];
    if (!yAxis) return;

    yAxis.update({ plotLines: allPlotLines }, false);
    chart.redraw(false);
  }, [allPlotLines]);

  /* ----------------------------------------------- */
  /* ---------- { annotations 업데이트 파트 } ---------- */
  /* ------------------------------------------------*/

  // boxLines, tradeLabels 업데이트
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // boxLines → annotation 배열 한 방에 생성
    const boxAnnotations = (boxLines || [])
      .filter(
        (lineItem) =>
          boxLineTimes(lineItem).length >= 2 && !isNaN(lineItem.close_box),
      )
      .map((lineItem) => {
        // 봉우리 객체를 시간순으로 정렬한 뒤 x 좌표 추출
        const sortedTimes = boxLineTimes(lineItem);

        // 정렬된 밀리초 배열로 포인트 객체 생성
        const points = sortedTimes.map((x) => ({
          xAxis: 0,
          yAxis: 0,
          x,
          y: lineItem.close_box,
        }));

        return {
          id: `box-${lineItem.id}`, // run별 라인 ID로 안정적으로 갱신
          type: 'crookedLine', // 루트-레벨
          draggable: '',
          controlPointOptions: { visible: false },
          typeOptions: {
            points,
            line: boxLineStroke(lineItem),
          },
        };
      });

    // 자동거래 라벨(한 개의 annotation 그룹으로 묶음)
    const tradeLabelsAnnotation =
      (tradeLabels?.length || 0) > 0
        ? [
            {
              id: 'trade-labels',
              draggable: 'y',
              labels: tradeLabels,
              // 공통 labelOptions(선택)
              labelOptions: {
                allowOverlap: true, // 겹침 허용(필요 시)
                crop: false,
                overflow: 'justify',
              },
              zIndex: 7,
            },
          ]
        : [];

    // chart.update — oneToOne = true → id가 없는 예전 annotation 은 자동 제거
    chart.update(
      { annotations: [...boxAnnotations, ...tradeLabelsAnnotation] }, // 새 배열 통째로
      false, // redraw 나중에
      true, // oneToOne
    );
    chart.redraw(true);
  }, [boxLines, tradeLabels]);
  /* ------------------------ Top Volume scatter 오버레이(세션 범위로 필터) ------------------------ */

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const seriesId = 'topVolumeScatter';

    const sStart = sessionStartMsRef.current;
    const sEnd = sessionEndMsRef.current;

    const points = [];
    Object.entries(visiblePlotLines || {}).forEach(([_, val]) => {
      if (!isLineChecked(val)) return;
      if (typeof val !== 'object' || val === null) return;

      const { timestamp_korean, close, interval, rank, volume, power } = val;
      if (!timestamp_korean || close == null) return;

      const x = new Date(timestamp_korean).getTime();
      if (Number.isFinite(sStart) && Number.isFinite(sEnd)) {
        if (x < sStart || x > sEnd) return; // ✅ 현재 세션 범위만 표시
      }

      const y = Number(close);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      points.push({
        x,
        y,
        interval: interval || null,
        rank: rank != null ? rank : null,
        volume: Number(volume),
        power: Number(power),
      });
    });

    let scatterSeries = chart.series.find(
      (s) => s.userOptions && s.userOptions.id === seriesId,
    );

    if (!scatterSeries) {
      scatterSeries = chart.addSeries(
        {
          id: seriesId,
          type: 'scatter',
          name: '시간대별 체결량 포인트',
          data: points,
          yAxis: 0,
          zIndex: 5,
          marker: { symbol: 'circle', radius: 5 },
          states: { inactive: { opacity: 1, enabled: false } },
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            formatter: function () {
              const interval = this.point.interval || '';
              const rank = this.point.rank;
              const v = this.point.volume;
              const p = this.point.power;

              const vTxt = Number.isFinite(v)
                ? Highcharts.numberFormat(v, 0)
                : '-';
              const pTxt = Number.isFinite(p)
                ? Highcharts.numberFormat(p, 0)
                : '-';

              const rankTxt = rank != null ? `${rank}위` : '';
              const intervalTxt = interval ? `${interval}` : '';
              const header =
                intervalTxt || rankTxt
                  ? `[${[intervalTxt, rankTxt].filter(Boolean).join(' / ')}]`
                  : '';

              return [header, `V:${vTxt}`, `P:${pTxt}`]
                .filter(Boolean)
                .join('\n');
            },
            style: { fontSize: '11px', textOutline: 'none' },
          },
          tooltip: {
            valueDecimals: 2,
            pointFormatter: function () {
              const dt = Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', this.x);
              const price = Highcharts.numberFormat(this.y, 2);
              const v = Number.isFinite(this.point.volume)
                ? Highcharts.numberFormat(this.point.volume, 0)
                : '-';
              const p = Number.isFinite(this.point.power)
                ? Highcharts.numberFormat(this.point.power, 0)
                : '-';
              const interval =
                this.point.interval != null ? String(this.point.interval) : '-';
              const rank =
                this.point.rank != null ? String(this.point.rank) : '-';
              return (
                `발생시각: ${dt}<br/>` +
                `종가: ${price}<br/>` +
                `체결량: ${v}<br/>` +
                `강도: ${p}<br/>` +
                `시간대: ${interval}<br/>` +
                `순위: ${rank}`
              );
            },
          },
        },
        false,
      );
    } else {
      scatterSeries.setData(points, false);
    }

    chart.redraw(false);
  }, [visiblePlotLines]);

  /* ------------------------ Power(Custom START Range + RESET) ------------------------ */

  function ensureZeroPlotLine(chart) {
    chart.yAxis[1].update(
      {
        plotLines: [
          {
            color: 'black',
            value: 0,
            width: 1,
            zIndex: 5,
            dashStyle: 'dash',
            label: { text: '0', align: 'right', style: { color: 'grey' } },
          },
        ],
      },
      false,
    );
  }

  function getCloseRelYRaw(close) {
    const chart = chartRef.current?.chart;
    if (!chart || close == null) return null;
    const yPx = chart.yAxis[0].toPixels(close);
    const yPlot = yPx - chart.plotTop;
    if (!Number.isFinite(yPlot) || chart.plotHeight <= 0) return null;
    const rel = yPlot / chart.plotHeight;
    return Math.max(0, Math.min(1, rel));
  }

  function getYAxis1HeightPct(chart) {
    const y1 = chart.yAxis?.[1];
    if (!y1) return 30;
    const h = y1.options?.height ?? '30%';
    if (typeof h === 'string' && h.endsWith('%')) {
      const v = parseFloat(h);
      return Number.isFinite(v) ? v : 30;
    }
    if (typeof h === 'number' && chart.plotHeight > 0)
      return (h / chart.plotHeight) * 100;
    return 30;
  }

  function positionYAxis1ZeroNearClose(closeCandidate) {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    let close = closeCandidate;
    if (close == null) {
      const s0 =
        chart.series?.find((s) => s.userOptions?.id === 'priceSeries') ||
        chart.series?.[0];
      const last = s0?.points?.at(-1) ?? s0?.data?.at(-1);
      close = last?.close ?? last?.y ?? (Array.isArray(last) ? last[4] : null);
    }

    const rel = getCloseRelYRaw(close);
    if (rel == null) return;

    const y1 = chart.yAxis[1];
    const heightPct = getYAxis1HeightPct(chart);

    const closePct = rel * 100;
    const gapPct = (ZERO_GAP_PX / chart.plotHeight) * 100;

    let centerPct = closePct - gapPct;
    let topPct = centerPct - heightPct / 2;

    const maxTop = 100 - heightPct;
    if (!Number.isFinite(topPct)) topPct = 0;
    topPct = Math.max(0, Math.min(maxTop, topPct));

    y1.update({ top: topPct.toFixed(4) + '%' }, false);
  }

  function clearRangeMarkers() {
    const chart = chartRef.current?.chart;
    const xa = chart?.xAxis?.[0];
    if (!chart || !xa) return;

    const { startLineId, endLineId, bandId } = rangeMarkersRef.current;
    if (startLineId) xa.removePlotLine(startLineId);
    if (endLineId) xa.removePlotLine(endLineId);
    if (bandId) xa.removePlotBand(bandId);

    rangeMarkersRef.current = {
      startLineId: null,
      endLineId: null,
      bandId: null,
    };
    rangeStartXRef.current = null;
    rangeEndXRef.current = null;
  }

  function resetPowerOnly() {
    const chart = chartRef.current?.chart;
    setPowerActive(false);
    setCurrentCumulativePower(0);
    setPowerMinMax({ minPower: 0, maxPower: 0 });
    clearRangeMarkers();

    if (chart?.series?.[1]) chart.series[1].setData([], false);
    if (chart?.yAxis?.[1])
      chart.yAxis[1].update({ plotLines: [], min: -50, max: 50 }, false);
    chart?.redraw?.(false);
  }

  async function prefillPowerRange(startXms, endXms) {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    ensureZeroPlotLine(chart);
    positionYAxis1ZeroNearClose();

    const startIsoKst = getKSTIsoString(new Date(startXms));
    const endIsoKst = getKSTIsoString(new Date(endXms));

    let rows = [];
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
        {
          params: { start: startIsoKst, end: endIsoKst },
        },
      );
      rows = Array.isArray(data) ? data : [];
    } catch (e) {
      // end 미지원 fallback
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/data/ls_nasdaq_ohlcp/range`,
          {
            params: { start: startIsoKst },
          },
        );
        rows = (Array.isArray(data) ? data : []).filter((r) => {
          const t = new Date(r.timestamp_korean).getTime();
          return t <= endXms;
        });
      } catch (e2) {
        console.error('custom range fetch 실패', e2);
        alert('데이터를 불러오지 못했습니다.');
        return;
      }
    }

    if (!rows.length) {
      alert('선택한 구간에 데이터가 없습니다.');
      return;
    }

    rows.sort(
      (a, b) => new Date(a.timestamp_korean) - new Date(b.timestamp_korean),
    );

    let cum = 0;
    let minP = 0;
    let maxP = 0;

    const points = rows.map((r) => {
      const x = new Date(r.timestamp_korean).getTime();
      const p = parseInt(r.power) || 0;
      cum += p;
      if (cum < minP) minP = cum;
      if (cum > maxP) maxP = cum;
      return [x, cum];
    });

    const powerSeries = chart.series?.[1];
    if (powerSeries) powerSeries.setData(points, false);

    const rangeAbs = Math.max(Math.abs(minP), Math.abs(maxP), 50);
    chart.yAxis[1].update({ min: -rangeAbs, max: rangeAbs }, false);

    chart.redraw(true);

    setPowerActive(true);
    setCurrentCumulativePower(cum);
    setPowerMinMax({ minPower: minP, maxPower: maxP });
  }

  function onChartClick(e) {
    if (!customRangeArmedRef.current) return;

    const chart = chartRef.current?.chart;
    const xaObj = e?.xAxis?.[0];
    if (!chart || !xaObj || xaObj.value == null) return;

    const clickX = Math.round(xaObj.value);
    const xAxis = chart.xAxis[0];

    if (rangePhaseRef.current === 'start') {
      rangeStartXRef.current = clickX;

      const startId = 'customRangeStart';
      xAxis.removePlotLine(startId);
      xAxis.addPlotLine({
        id: startId,
        value: clickX,
        color: '#0d6efd',
        width: 1,
        zIndex: 5,
      });
      rangeMarkersRef.current.startLineId = startId;

      setRangeSelectPhase('end');
      return;
    }

    if (rangePhaseRef.current === 'end') {
      rangeEndXRef.current = clickX;

      const endId = 'customRangeEnd';
      xAxis.removePlotLine(endId);
      xAxis.addPlotLine({
        id: endId,
        value: clickX,
        color: '#dc3545',
        width: 1,
        zIndex: 5,
      });
      rangeMarkersRef.current.endLineId = endId;

      const s = Math.min(rangeStartXRef.current, clickX);
      const t = Math.max(rangeStartXRef.current, clickX);
      if (s === t) return;

      const bandId = 'customRangeBand';
      xAxis.removePlotBand(bandId);
      xAxis.addPlotBand({
        id: bandId,
        from: s,
        to: t,
        color: 'rgba(13,110,253,0.08)',
        zIndex: 3,
      });
      rangeMarkersRef.current.bandId = bandId;

      // 무장해제 & 계산
      setCustomRangeArmed(false);
      setRangeSelectPhase('idle');

      prefillPowerRange(s, t);
    }
  }

  // 차트 클릭 이벤트 바인딩(차트 생성 후)
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    chart.update({ chart: { events: { click: onChartClick } } }, false);
    chart.redraw(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartOptions]); // 차트가 재생성/갱신될 때 재바인딩

  // 커서 모양
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !chart.container) return;
    chart.container.style.cursor = customRangeArmed ? 'crosshair' : 'default';
  }, [customRangeArmed]);

  // ESC 취소: (1) 커스텀레인지 취소, (2) 모달 닫기
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return;

      if (customRangeArmedRef.current) {
        setCustomRangeArmed(false);
        setRangeSelectPhase('idle');
        clearRangeMarkers();
      }
      setSimListOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* ------------------------ 최초 1회: 오늘 세션 로드 ------------------------ */

  useEffect(() => {
    loadSessionByDate(targetDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 최초 1회만

  return (
    <div>
      {/* 상단: SIM overlay 컨트롤 + 세션 이동 */}
      <div className='flex items-center gap-3 flex-wrap m-3 pl-2'>
        <span className='text-sm font-medium'>시뮬레이션</span>
        <button
          className='border px-2 py-1 text-sm font-medium'
          onClick={() => setSimListOpen(true)}
        >
          목록
        </button>

        <input
          className='border px-2 py-1 text-sm w-24'
          value={simRunId}
          onChange={(e) => {
            calculationRunIdRef.current = null;
            setSimRunId(e.target.value);
          }}
          onBlur={async () => {
            if (!simRunId) {
              calculationRunIdRef.current = null;
              loadSessionByDate(targetDate);
              return;
            }
            try {
              const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/sim/runs/${simRunId}`,
              );
              calculationRunIdRef.current = data.calculation_run_id || null;
              const dateStr = data.start_kst
                ? String(data.start_kst).slice(0, 10)
                : targetDate;
              setTargetDate(dateStr);
              loadSessionByDate(dateStr);
            } catch (error) {
              console.error('시뮬레이션 run 조회 실패:', error);
            }
          }}
          placeholder='run_id'
        />

        <label className='text-sm flex items-center gap-1'>
          <input
            type='checkbox'
            checked={simEnabled}
            onChange={(e) => setSimEnabled(e.target.checked)}
          />
          오버레이
        </label>

        <label className='text-xs flex items-center gap-1'>
          <input
            type='checkbox'
            checked={includeBoxlines}
            onChange={(e) => setIncludeBoxlines(e.target.checked)}
            disabled={!simEnabled}
          />
          boxlines
        </label>

        <label className='text-xs flex items-center gap-1'>
          <input
            type='checkbox'
            checked={includeRisk}
            onChange={(e) => setIncludeRisk(e.target.checked)}
            disabled={!simEnabled}
          />
          risk
        </label>

        <label className='text-xs flex items-center gap-1'>
          <input
            type='checkbox'
            checked={includeOrders}
            onChange={(e) => setIncludeOrders(e.target.checked)}
            disabled={!simEnabled}
          />
          fills
        </label>

        <button
          className='border px-2 py-1 text-xs'
          onClick={() => refreshSimOverlay()}
          disabled={!simEnabled || !simRunId}
        >
          새로고침
        </button>

        <button
          className='border px-2 py-1 text-xs'
          onClick={() => removeSimOverlay()}
        >
          제거
        </button>

        {simLoading && <span className='text-xs'>loading…</span>}

        <div className='ml-2 flex items-center gap-2'>
          <span className='text-sm font-medium'>세션 날짜</span>
          <input
            type='date'
            className='border px-2 py-1 text-sm'
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
          <button
            className='border px-3 py-1 text-sm'
            onClick={() => {
              calculationRunIdRef.current = null;
              setSimRunId('');
              loadSessionByDate(targetDate);
            }}
            disabled={loadingSession || !targetDate}
          >
            {loadingSession ? '로딩중…' : '이동'}
          </button>
        </div>
      </div>

      <div className='flex flex-wrap justify-center items-end pl-2'>
        {/* 왼쪽: 차트 */}
        <div className='flex-1 min-w-[30rem]'>
          {chartOptions.series && (
            <HighchartsReact
              highcharts={Highcharts}
              constructorType='stockChart'
              options={chartOptions}
              ref={chartRef}
            />
          )}
        </div>

        {/* 오른쪽: 강도/컨트롤 */}
        <div className='flex-none w-[10rem] sm:w-[12rem] md:w-[14rem] lg:w-[16rem] flex flex-col items-center pb-4 my-auto'>
          {/* 강도 표시 카드 */}
          <div
            className='w-full max-w-[11rem] p-4 mt-8 rounded-lg text-white text-center shadow'
            style={{
              backgroundColor: !powerActive
                ? '#eee4ee'
                : currentCumulativePower < 0
                  ? '#007BFF'
                  : '#DC3545',
            }}
          >
            <p className='my-1'>최고 강도: {powerMinMax.maxPower}</p>
            <p className='my-1 font-bold text-[2.5rem]'>
              {powerActive ? currentCumulativePower : '-'}
            </p>
            <p className='my-1'>최저 강도: {powerMinMax.minPower}</p>
          </div>

          <p className='text-base font-bold'>
            {powerActive ? '구간 강도 표시중' : '-'}
          </p>

          {/* RESET */}
          <div className='flex gap-4 mt-4'>
            <button
              onClick={resetPowerOnly}
              disabled={!chartRef.current || !chartRef.current.chart}
              className='px-3 text-[1.4rem] font-semibold rounded-[5px] h-[4.2rem] text-white
              focus:outline-none focus:ring-2 focus:ring-sky-500
              disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: '#DC3545',
                cursor: 'pointer',
              }}
              title='강도 구간/표시를 초기화'
            >
              RESET
            </button>
          </div>

          {/* Custom START Range */}
          <div className='flex gap-4 mt-4'>
            <button
              onClick={() => {
                setCustomRangeArmed((v) => {
                  const armed = !v;
                  if (armed) {
                    clearRangeMarkers();
                    setRangeSelectPhase('start');
                  } else {
                    setRangeSelectPhase('idle');
                    clearRangeMarkers();
                  }
                  return armed;
                });
              }}
              disabled={!chartRef.current || !chartRef.current.chart}
              className='px-3 text-[1.2rem] font-semibold rounded-[5px] h-[4.2rem] text-white
               focus:outline-none focus:ring-2 focus:ring-sky-500
               disabled:opacity-60 disabled:cursor-not-allowed'
              style={{
                backgroundColor: customRangeArmed ? '#6c757d' : '#20c997',
                cursor: 'pointer',
              }}
              title='버튼을 누른 뒤 차트에서 시작점/끝점을 차례대로 클릭(ESC로 취소)'
            >
              {customRangeArmed
                ? rangeSelectPhase === 'start'
                  ? '구간 시작 클릭(ESC)'
                  : '구간 끝 클릭(ESC)'
                : 'Custom START Range'}
            </button>
          </div>
        </div>
      </div>

      {/* 하단 패널 */}
      <div className='flex py-4 items-start justify-center gap-3'>
        <AutoTradingDetailHistoryPage />
      </div>

      {/* ✅ SimRunsListTable 모달 */}
      {simListOpen && (
        <div
          className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/40'
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSimListOpen(false);
          }}
        >
          <div className='bg-white rounded-lg shadow-xl w-[28rem] max-w-[92vw] max-h-[85vh] overflow-hidden'>
            <div className='flex items-center justify-between px-4 py-2 border-b'>
              <div className='text-sm font-semibold'>시뮬레이션 실행 목록</div>
              <button
                className='text-sm border px-2 py-1'
                onClick={() => setSimListOpen(false)}
              >
                닫기
              </button>
            </div>

            <div className='p-3 overflow-auto max-h-[75vh]'>
              <SimRunsListTable
                selectedId={simRunId}
                onSelect={handleSelectSimRun}
                onClose={() => setSimListOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
